import { Fragment, useEffect, useState } from "react";
import { SvgIcon } from "@progress/kendo-react-common";
import {
  userIcon,
  groupIcon,
  chartLineMarkersIcon,
  fileIcon,
} from "@progress/kendo-svg-icons";
import SubscriptionPlan from "./tabs/subscriptionplan";
import { UserInfo } from "./tabs/userinfo";
import { BillingInvoice } from "./tabs/billinginvoice";
import { Dashboard } from "./tabs/dashboard";
import { useSelector } from "react-redux";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { DropDownList } from "@progress/kendo-react-dropdowns";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const AccountManagementPageBody = (props) => {
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const pageState = useSelector((state) => state.accountManagementPage);
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1024px)");
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);

    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);
    return () => mediaQuery.removeEventListener("change", updateIsMobile);
  }, []);

  const tabContent = [
    {
      label: t("accountmang.accountmang.dashboard"),
      icon: groupIcon,
      content: (
        <Dashboard
          onCloseAccountManagement={props.onCloseAccountManagement}
          onCancelSubsciption={props.onCancelSubsciption}
        />
      ),
    },
    {
      label: t("accountmang.accountmang.userinfo"),
      icon: userIcon,
      content: (
        <UserInfo
          updateDirtyState={props.updateDirtyState}
          handleOnChangeField={props.handleOnChangeField}
          onUpdateUserInfo={props.onUpdateUserInfo}
          onChangePasswordClick={props.onChangePasswordClick}
        />
      ),
    },
    {
      label: t("accountmang.accountmang.sub"),
      icon: chartLineMarkersIcon,
      content: <SubscriptionPlan onResizeClick={props.onResizeClick} />,
    },
  ];

  // Conditionally add the Billing tab
  if (currentUser?.IsServiceSubscriptionRootUser) {
    tabContent.push({
      label: t("accountmang.accountmang.billing"),
      icon: fileIcon,
      content: (
        <BillingInvoice
          onCancelSubsciption={props.onCancelSubsciption}
          onStoreClick={props.onStoreClick}
        />
      ),
    });
  }

  const tabTitle = (icon, label) => {
    return (
      <div style={{ display: "flex" }}>
        <SvgIcon icon={icon} size="small" style={{ margin: "4px" }} />{" "}
        <label>{label}</label>
      </div>
    );
  };

  const tabPositions = appearance.isRightToLeft
    ? [
        { text: t("accountmang.tabs.top"), value: "top" },
        { text: t("accountmang.tabs.right"), value: "right" },
      ]
    : [
        { text: t("accountmang.tabs.top"), value: "top" },
        { text: t("accountmang.tabs.left"), value: "left" },
      ];
  const selectedTabPosition = tabPositions.find(
    (position) => position.value === pageState.tabPosition,
  );
  const tabPosition = isMobile ? "top" : pageState.tabPosition;

  return (
    <Fragment>
      <DropDownList
        className="accountmanagement-tab-position"
        data={tabPositions}
        textField="text"
        dataItemKey="value"
        value={selectedTabPosition}
        onChange={(event) =>
          props.onChangeTabPosition({ value: event.value.value })
        }
        style={{
          position: "absolute",
          width: "150px",
          left: appearance.isRightToLeft ? "0" : "unset",
          right: appearance.isRightToLeft ? "unset" : "9px",
          zIndex: "1000",
        }}
      />

      <TabStrip
        selected={pageState.selectedTab}
        onSelect={props.onTabSelected}
        tabPosition={tabPosition}
      >
        {tabContent.map((tab, index) => (
          <TabStripTab
            title={tabTitle(tab.icon, tab.label)}
            index={index}
            key={index}
          >
            {tab.content}
          </TabStripTab>
        ))}
      </TabStrip>
      <style>{`
      
        .page.accountmanagement-page .k-tabstrip{
          width:100%; 
        }
         .accountmanagement-page .k-tabstrip-right > .k-tabstrip-items-wrapper > .k-tabstrip-items
        {
          direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
         }

          .accountmanagement-page .k-tabstrip-top > .k-tabstrip-items-wrapper > .k-tabstrip-items
        {
          direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
         }

          .accountmanagement-page .k-tabstrip-items-wrapper .k-item{
              color:${currentTheme.primary};
          }
               .accountmanagement-page .k-tabstrip-left>.k-tabstrip-items-wrapper .k-item.k-state-active,.accountmanagement-page .k-tabstrip-left>.k-tabstrip-items-wrapper .k-item.k-active{
              color:${currentTheme.touchColor};
          }
          // .accountmanagement-page  .k-tabstrip-left{height:100%}
           .accountmanagement-page .k-tabstrip-items-wrapper .k-item.k-active{
                border-color:rgba(0,0,0,0.2);
                border-bottom-width: ${
                  tabPosition == "top" ? "0" : "1px"
                };
                border-right-width: ${
                  tabPosition == "left" ? "0" : "1px"
                };   color:${currentTheme.touchColor};
            }
          .accountmanagement-page .k-tabstrip-items-wrapper{
              border-color:rgba(0,0,0,0.2);
           }

          @media (max-width: 1024px) {
            .page.accountmanagement-page > .content {
              box-sizing: border-box;
              overflow-x: hidden;
              padding-bottom: calc(80px + env(safe-area-inset-bottom));
            }

            .accountmanagement-page .accountmanagement-tab-position {
              display: none;
            }

            .accountmanagement-page .k-tabstrip-left,
            .accountmanagement-page .k-tabstrip-right {
              flex-direction: column;
            }

            .accountmanagement-page .k-tabstrip-left > .k-tabstrip-items-wrapper,
            .accountmanagement-page .k-tabstrip-right > .k-tabstrip-items-wrapper {
              width: 100%;
            }

            .accountmanagement-page .k-tabstrip-left > .k-tabstrip-items-wrapper > .k-tabstrip-items,
            .accountmanagement-page .k-tabstrip-right > .k-tabstrip-items-wrapper > .k-tabstrip-items,
            .accountmanagement-page .k-tabstrip-top > .k-tabstrip-items-wrapper > .k-tabstrip-items {
              display: flex;
              flex-direction: row;
              max-width: 100%;
              overflow-x: auto;
              white-space: nowrap;
            }

            .accountmanagement-page .k-tabstrip-content {
              box-sizing: border-box;
              min-width: 0;
              padding: 12px;
              width: 100%;
            }
          }
          `}</style>
    </Fragment>
  );
};
